import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { apiFetch } from '@/utils/api';
import { toastApiError } from '@/lib/query-client';
import { useApp } from '@/components/hooks/useApp';
import { useAccountingFirmContext } from '@/components/hooks/useAccountingFirmContext';
import { usePortalBasePath } from '@/components/hooks/useCanSeePortalAsesor';

// Sprint 5.bis.3 — Modo Dios banner.
//
// SEMÁNTICA: el banner se OCULTA visualmente en /portal-asesor (hide en
// pathname check) pero NO limpia el estado backend. Si el user navega
// manualmente al portal (sin Esc/click "Volver"), currentCompany sigue
// seteado y el banner reaparece al volver a cualquier otra ruta. Para
// SALIR del Modo Dios (limpiar estado + users.company_id=null), el user
// debe usar Esc o "Volver al portal" del banner.
// Asimetría intencional: navegación = "echo un vistazo, vuelvo";
// Esc/Volver = "salgo de verdad".

const SESSION_START_KEY_PREFIX = 'god_session_start:';
const TIME_TICK_MS = 60_000;

function formatRelative(startedAtMs: number, locale = 'es'): string {
  const deltaSeconds = Math.round((startedAtMs - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(deltaSeconds);
  if (abs < 60) return rtf.format(Math.round(deltaSeconds), 'second');
  if (abs < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  return rtf.format(Math.round(deltaSeconds / 86400), 'day');
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

export default function GodBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const portalBasePath = usePortalBasePath();

  const { currentCompany } = useApp();
  const { firm, isAsesor, isLoading } = useAccountingFirmContext();

  const visible =
    !isLoading &&
    isAsesor &&
    !!currentCompany?.id &&
    !pathname.startsWith('/portal-asesor') &&
    !pathname.startsWith('/mis-empresas');

  // ── exit flow (shared by Esc + click) ─────────────────────────────────────
  const exitMutation = useMutation({
    mutationFn: () => apiFetch('/api/auth/exit-company', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries();
      if (currentCompany?.id) {
        sessionStorage.removeItem(SESSION_START_KEY_PREFIX + currentCompany.id);
      }
      // replace: true evita que el back-button del navegador devuelva al user
      // a la company de la que acaba de salir (estado backend ya limpio =
      // inconsistencia).
      navigate(portalBasePath, { replace: true });
    },
    onError: (err) => {
      // Mantener banner visible — si navegásemos sin limpiar BE, user llegaría
      // al portal con company_id aún seteado en cookie/cache (estado fantasma).
      toastApiError(err, t('asesor.god.exit_error'));
    },
  });

  // ── session time tracker (setInterval explícito, no recálculo por render) ─
  // `_now` triggera re-render cada 60s para refrescar el texto relativo.
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    if (!visible) return;
    const id = window.setInterval(() => setNow(Date.now()), TIME_TICK_MS);
    return () => window.clearInterval(id);
  }, [visible]);

  const sessionStart = useMemo(() => {
    if (!visible || !currentCompany?.id) return null;
    const key = SESSION_START_KEY_PREFIX + currentCompany.id;
    const stored = sessionStorage.getItem(key);
    if (stored) return Number(stored);
    const t0 = Date.now();
    sessionStorage.setItem(key, String(t0));
    return t0;
  }, [visible, currentCompany?.id]);

  const relTime = sessionStart != null
    ? formatRelative(sessionStart, i18n.language?.startsWith('en') ? 'en' : 'es')
    : '';

  // ── Esc hotkey listener (mounted ONLY when visible) ───────────────────────
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isEditableTarget(e.target)) return; // skip inputs / textareas / contentEditable
      if (exitMutation.isPending) return;
      e.preventDefault();
      exitMutation.mutate();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible, exitMutation]);

  if (!visible) return null;

  // Sufijo "como su asesoría X" SOLO cuando la company tiene este firm
  // asignado. Si la company NO tiene firm (o pertenece a OTRO firm), mostrar
  // solo "Estás gestionando [Company]" para evitar información engañosa.
  const companyFirmId =
    (currentCompany as { accounting_firm_id?: string | null })
      ?.accounting_firm_id ?? null;
  const showFirmSuffix = !!firm && companyFirmId === firm.id;

  return (
    <div
      data-testid="god-banner"
      role="status"
      aria-live="polite"
      className="god-banner-active sticky top-0 z-40 flex items-center justify-center px-6"
      style={{
        height: 64,
        color: 'var(--god-fg)',
        background: 'var(--god-grad)',
        backdropFilter: 'var(--god-blur)',
        WebkitBackdropFilter: 'var(--god-blur)',
        boxShadow: 'var(--god-shadow)',
        border: 'var(--god-border)',
        fontSize: 13,
        fontWeight: 500,
        textShadow: '0 1px 2px rgba(0,0,0,0.20)',
      }}
    >
      <div className="flex items-center gap-3 flex-wrap justify-center text-center max-w-7xl">
        <span aria-hidden="true">🔮</span>
        <span className="font-semibold">{t('asesor.god.label')}</span>
        <span aria-hidden="true">·</span>
        <span>{t('asesor.god.active', { company_name: currentCompany?.name ?? '' })}</span>
        {showFirmSuffix && (
          <>
            <span>{t('asesor.god.firm_prefix')}</span>
            <strong>{firm!.name}</strong>
          </>
        )}
        <span aria-hidden="true">·</span>
        <span>{t('asesor.god.session_since', { time: relTime })}</span>
        <button
          type="button"
          data-testid="god-exit"
          onClick={() => exitMutation.mutate()}
          disabled={exitMutation.isPending}
          className="ml-2 inline-flex items-center gap-2 rounded-md px-3 py-1 text-xs font-medium underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-70"
          style={{ color: 'var(--god-fg)' }}
        >
          {exitMutation.isPending && (
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          )}
          {exitMutation.isPending ? t('asesor.god.exit_loading') : t('asesor.god.exit')}
        </button>
      </div>
    </div>
  );
}
